/**
 * Client for the policy management API (/api/policies, /api/policy-options).
 *
 * Dates arrive as ISO 8601 strings. Policy content is hashed and versioned by
 * the rules in @shared/policy-core, which the API enforces as well.
 */

import { apiDelete, apiGet, apiPost, apiPut } from '@/services/api';
import type { BumpType, PolicyScope, VersionChange } from '@shared/policy-core';

export type { BumpType, PolicyScope, VersionChange } from '@shared/policy-core';

export interface FeatureGroupOption {
  id: string;
  name: string;
}

export interface FeatureKeyOption {
  id: string;
  name: string;
  featureGroup: string | null;
  keyValue: string | null;
}

export interface PolicyOptions {
  groups: FeatureGroupOption[];
  keys: FeatureKeyOption[];
}

export interface PolicyPerson {
  uid: string;
  email: string | null;
}

export interface DraftSummary {
  bump: BumpType | null;
  nextVersion: string;
  /** Another version was published after this draft was started. */
  stale: boolean;
  modified: string | null;
  updatedBy: PolicyPerson | null;
}

interface PolicyLinkage {
  policyScope: PolicyScope;
  /** policyScope is missing on the document and was inferred like the mobile app does. */
  scopeInferred: boolean;
  featureGroupId?: string | null;
  featureKeyId?: string | null;
  featureGroupName: string | null;
  featureKeyName: string | null;
}

export interface PolicyListItem extends PolicyLinkage {
  id: string;
  title: string;
  description: string;
  policyType: string;
  isRequired: boolean;
  published: boolean;
  currentVersion?: string;
  publishedAt?: string;
  publicUrl?: string;
  draft: DraftSummary | null;
  issues: string[];
}

export interface Policy extends PolicyLinkage {
  id: string;
  title: string;
  description: string;
  policyType: string;
  isRequired: boolean;
  currentVersion: string;
  currentMajorVersion: number;
  currentMinorVersion: number;
  currentPatchVersion: number;
  currentContentHash: string;
  publicUrl?: string;
  publishedAt?: string;
}

export interface PolicyVersionSummary {
  id: string;
  version: string;
  majorVersion: number;
  minorVersion: number;
  patchVersion: number;
  changeType?: VersionChange;
  title: string;
  contentHash: string;
  contentHashValid: boolean;
  summaryOfChanges: string[];
  changelog?: string;
  publicUrl?: string;
  effectiveDate?: string;
  publishedDate?: string;
  publishedBy?: PolicyPerson;
}

export interface PolicyVersion extends PolicyVersionSummary {
  contentMarkdown: string;
  computedHash: string;
}

export interface PolicyDraft extends PolicyLinkage {
  id: string;
  isNewPolicy: boolean;
  title: string | null;
  description?: string;
  policyType?: string;
  isRequired?: boolean;
  contentMarkdown: string;
  bump: BumpType | null;
  baseVersion: string | null;
  summaryOfChanges: string[];
  changelog: string | null;
  effectiveDate: string | null;
  updatedBy: PolicyPerson | null;
  modified: string | null;
  summary: DraftSummary;
}

export interface PolicyDetail {
  policy: Policy | null;
  draft: PolicyDraft | null;
  versions: PolicyVersionSummary[];
  issues: string[];
}

export interface DraftInput {
  title: string;
  contentMarkdown: string;
  summaryOfChanges: string[];
  changelog?: string;
  effectiveDate?: string;
  /** Published policies only. */
  bump?: BumpType;
  /** New policies only. */
  description?: string;
  policyType?: string;
  policyScope?: PolicyScope;
  featureGroupId?: string;
  featureKeyId?: string;
  isRequired?: boolean;
}

export interface MetadataInput {
  description?: string;
  isRequired?: boolean;
  policyType?: string;
  featureGroupId?: string;
  featureKeyId?: string;
}

/** SRS §7 PublishPolicyVersionRequest, plus the hash the editor reviewed. */
export interface PublishRequest {
  version: string;
  majorVersion: number;
  minorVersion: number;
  patchVersion: number;
  title: string;
  contentMarkdown: string;
  summaryOfChanges: string[];
  changelog?: string;
  effectiveDate: string;
  expectedContentHash?: string;
}

export interface PublishResult {
  success: boolean;
  policyId: string;
  version: string;
  contentHash: string;
  publicUrl: string;
  publishedAt: string;
}

export interface Acceptance {
  id: string;
  userId: string;
  companyId?: string;
  policyId: string;
  policyTitle?: string;
  acceptedVersion: string;
  contentHash: string;
  acceptanceMethod?: string;
  appVersion?: string;
  platform?: string;
  acceptedAt?: string;
  recordedAt?: string;
  versionKnown: boolean;
  hashMatches: boolean;
}

export interface AcceptancePage {
  items: Acceptance[];
  nextCursor: string | null;
  /** 'documentId' until the composite index on acceptedAt exists. */
  ordering: 'acceptedAt' | 'documentId';
  indexUrl: string | null;
}

export interface AcceptanceStats {
  total: number;
  byVersion: { version: string; count: number }[];
}

export interface AcceptanceFilters {
  version?: string;
  companyId?: string;
}

const base = (policyId: string) => `/api/policies/${encodeURIComponent(policyId)}`;

export const policyKeys = {
  all: ['/api/policies'] as const,
  options: ['/api/policy-options'] as const,
  detail: (id: string) => ['/api/policies', id] as const,
  version: (id: string, version: string) => ['/api/policies', id, 'versions', version] as const,
  acceptances: (id: string, filters: AcceptanceFilters) => ['/api/policies', id, 'acceptances', filters] as const,
  stats: (id: string) => ['/api/policies', id, 'acceptance-stats'] as const,
};

export const getPolicyOptions = () => apiGet<PolicyOptions>('/api/policy-options');
export const listPolicies = () => apiGet<PolicyListItem[]>('/api/policies');
export const getPolicy = (id: string) => apiGet<PolicyDetail>(base(id));
export const getPolicyVersion = (id: string, version: string) =>
  apiGet<PolicyVersion>(`${base(id)}/versions/${encodeURIComponent(version)}`);
export const updatePolicyMetadata = (id: string, input: MetadataInput) => apiPut<PolicyDetail>(base(id), input);
export const saveDraft = (id: string, input: DraftInput, { create = false } = {}) =>
  apiPut<PolicyDetail>(`${base(id)}/draft${create ? '?create=1' : ''}`, input);
export const discardDraft = (id: string) => apiDelete(`${base(id)}/draft`);
export const publishPolicyVersion = (id: string, request: PublishRequest) =>
  apiPost<PublishResult>(`${base(id)}/publish`, request);
export const getAcceptanceStats = (id: string) => apiGet<AcceptanceStats>(`${base(id)}/acceptance-stats`);

export function listAcceptances(id: string, filters: AcceptanceFilters, cursor?: string | null) {
  const params: Record<string, string> = {};
  if (filters.version) params.version = filters.version;
  if (filters.companyId) params.companyId = filters.companyId;
  if (cursor) params.cursor = cursor;
  return apiGet<AcceptancePage>(`${base(id)}/acceptances`, params);
}

// ── Display helpers ──────────────────────────────────────────────────────────

export const SCOPE_LABELS: Record<PolicyScope, string> = {
  global: 'Global',
  feature_group: 'Feature group',
  feature_key: 'Feature',
};

/** FR-2 user impact of each version change, as shown to authors. */
export const CHANGE_IMPACT: Record<VersionChange, { label: string; impact: string }> = {
  initial: {
    label: 'Initial version',
    impact: 'Required policies are shown to every user who has not accepted them, and block use until accepted.',
  },
  major: {
    label: 'Major',
    impact: 'Users must accept again. Required policies block the app or feature until they do.',
  },
  minor: {
    label: 'Minor',
    impact: 'Users see a non-blocking notice with the summary of changes. Nothing is blocked.',
  },
  patch: {
    label: 'Patch',
    impact: 'Silent correction (typos, formatting). Users are not notified.',
  },
};

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** ISO date-time → "YYYY-MM-DD" for a date input (UTC). */
export function toDateInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
}

/** "YYYY-MM-DD" from a date input → ISO date-time at UTC midnight. */
export function fromDateInput(value: string): string | undefined {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00.000Z` : undefined;
}

function csvCell(value: unknown): string {
  const text = value === null || value === undefined ? '' : String(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** CSV of consent records (RFC 4180 quoting). */
export function acceptancesToCsv(rows: Acceptance[]): string {
  const columns: (keyof Acceptance)[] = [
    'id', 'userId', 'companyId', 'policyId', 'acceptedVersion', 'contentHash', 'hashMatches',
    'acceptanceMethod', 'platform', 'appVersion', 'acceptedAt', 'recordedAt',
  ];
  return [columns.join(','), ...rows.map(row => columns.map(c => csvCell(row[c])).join(','))].join('\r\n');
}
